import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useStore } from 'zustand';
import { useSelectedWorkspace } from '../../stores/workspaceStore';
import { routeBase, studyName } from '../api/collections';
import { fetchStudyHead, studyHeadKey } from '../api/studyHead';
import { Button, TextLink } from '../ui/Button';
import { Notice } from '../ui/Controls';
import { Field, Input } from '../ui/Field';
import { IconAlertTriangle, IconApplyAll, IconCamera, IconCheck, IconFileText, IconMicroscope } from '../ui/icons';
import { toast } from '../ui/toast';
import { Breadcrumb } from '../upload/UploadStudyPage';
import { ImageFields, StudyFields, type FieldHint } from '../upload/DetailsFields';
import { STUDY, changedStudyProblems, createStudyEditor, isEditable, refusedCount, type DetailsHead, type ImageRow, type StudyEditor } from './store';

/**
 * The details editor of a study (`/studies/:studyId/details`): the uploader's
 * Study card and Images card for a study that already exists, without the
 * files. Every change is saved by itself (store.ts); Done sends what is still
 * waiting and goes back to the study.
 *
 * Only an admin or member of the study's workspace gets the form (the head's
 * `editable`). Anyone else who opens the address, or a study that is gone,
 * gets a page that says so; the server refuses their saves regardless.
 */
export function StudyDetailsPage() {
  const { studyId } = useParams<{ studyId: string }>();
  const head = useQuery({
    queryKey: studyHeadKey(studyId),
    queryFn: () => fetchStudyHead<DetailsHead>(studyId!),
    enabled: !!studyId,
  });
  if (!studyId || head.isPending) return <div style={{ flex: 1, background: '#fafaf9' }} />;
  if (!head.data) {
    return head.isError ? (
      <CannotEdit title="This study could not be loaded" body="The server could not be reached. Try again in a moment." />
    ) : (
      <CannotEdit title="You can’t edit this study" body="It is private to another workspace, or it no longer exists." />
    );
  }
  if (!head.data.editable) {
    return <CannotEdit title="You can’t edit this study" body="Only members and admins of its workspace can change its details. You can still view it." studyId={head.data.public_id} />;
  }
  return <Editor key={studyId} head={head.data} />;
}

/** No form: why, and the way on (the study, when the person can view it). */
export function CannotEdit({ title, body, studyId }: { title: string; body: string; studyId?: string }) {
  const nav = useNavigate();
  return (
    <div style={{ flex: 1, background: '#fafaf9', display: 'grid', placeItems: 'center', padding: 28 }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center', maxWidth: 420 }}>
        <span style={{ fontSize: 16, fontWeight: 600 }}>{title}</span>
        <span style={{ fontSize: 13.5, color: '#6b6b70', lineHeight: 1.5 }}>{body}</span>
        <Button variant="primary" size={34} style={{ marginTop: 6 }} onClick={() => nav(studyId ? `${routeBase()}/studies/${encodeURIComponent(studyId)}` : `${routeBase()}/collections`)}>
          {studyId ? 'Back to the study' : 'Back to collections'}
        </Button>
      </div>
    </div>
  );
}

/** Where an image is, in the rail: one line, in the upload page's colours. */
function rowStatus(r: ImageRow): { label: string; color: string } {
  switch (r.stage) {
    case 'published':
      // A published image has its site, but one retired from the list is
      // emptied (0091): it asks for another.
      return r.fields.site ? { label: 'Imported', color: '#2f6b3a' } : { label: 'Needs details', color: '#9a6400' };
    case 'needs-details':
      // With its site it is published in a moment: still importing.
      return r.fields.site ? { label: 'Importing', color: '#8a8a8f' } : { label: 'Needs details', color: '#9a6400' };
    case 'failed':
      return { label: 'Could not be imported', color: '#d9342b' };
    case 'converting':
    case 'ingesting':
      return { label: `Importing · ${r.percent ?? 0}%`, color: '#8a8a8f' };
    default:
      return { label: 'Uploading', color: '#8a8a8f' };
  }
}

/** The refusals shown beside a field; one about anything else goes under the fields. */
const STUDY_SHOWN = ['study_id', 'age', 'diagnosis', 'description', 'study_datetime'];
const IMAGE_SHOWN = ['site', 'laterality', 'type', 'stain', 'description'];
const red = (text: string | null | undefined): ReactNode => (text ? <span style={{ color: '#d9342b' }}>{text}</span> : undefined);

function Editor({ head }: { head: DetailsHead }) {
  const nav = useNavigate();
  const { workspaces, current } = useSelectedWorkspace();
  const [editor] = useState<StudyEditor>(() => createStudyEditor(head.public_id));
  const s = useStore(editor);
  const [leaving, setLeaving] = useState(false);
  const [held, setHeld] = useState(false);

  useEffect(() => {
    editor.getState().open(head);
    return () => editor.getState().close();
    // Opened once per study (the page is keyed by it); newer heads are taken below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);
  useEffect(() => editor.getState().takeHead(head), [editor, head]);

  const toStudy = () => nav(`${routeBase()}/studies/${encodeURIComponent(head.public_id)}`);
  const back = () => nav(head.collection_id ? `${routeBase()}/collections/${head.collection_id}` : `${routeBase()}/collections`);
  const wsName = workspaces.find((w) => w.public_id === head.workspace)?.name ?? current?.name ?? '';
  const refused = refusedCount(s);

  const done = async () => {
    setLeaving(true);
    await editor.getState().flush();
    setLeaving(false);
    if (refusedCount(editor.getState())) {
      setHeld(true);
      return;
    }
    toStudy();
  };

  // Said at a field once the person changed it: a study without a Study ID is not scolded on opening.
  const problems = changedStudyProblems(s.study, s.studySaved);
  const saidStudy: FieldHint = (field) => red(s.studyError?.field === field ? s.studyError.message : problems[field as keyof typeof problems]);
  const row = s.images?.find((r) => r.imageId === s.selectedId) ?? null;
  const saidImage = (r: ImageRow): FieldHint => (field, hint) => red(r.error?.field === field ? r.error.message : hint ? `Remove ${hint}.` : null);
  const editableCount = s.images?.filter(isEditable).length ?? 0;

  const status = refused ? (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#d9342b' }}>
      <IconAlertTriangle size={14} sw={2.4} />
      {refused === 1 ? 'A change is not saved: see the field marked in red' : `${refused} changes are not saved: see the fields marked in red`}
    </span>
  ) : s.pending || leaving ? (
    <span style={{ color: '#8a8a8f' }}>Saving…</span>
  ) : s.saved ? (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#2f6b3a' }}>
      <IconCheck size={14} sw={2.4} />
      Saved
    </span>
  ) : (
    <span style={{ color: '#8a8a8f' }}>Changes are saved as you type</span>
  );

  const railRow = (r: ImageRow) => {
    const on = r.imageId === s.selectedId;
    const { label, color } = rowStatus(r);
    return (
      <div key={r.imageId} className="tr-hover-white" onClick={() => editor.getState().select(r.imageId)} style={{ display: 'grid', gridTemplateColumns: '34px minmax(0,1fr)', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer', background: on ? '#fff' : 'transparent', boxShadow: on ? 'inset 3px 0 0 #0f0f10' : 'none', borderBottom: '1px solid #f0f0ee' }}>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: '#f2f2f0', display: 'grid', placeItems: 'center' }}>
          {r.kind === 'wsi' ? <IconMicroscope size={18} color="#0f0f10" /> : r.kind === 'document' ? <IconFileText size={18} color="#0f0f10" /> : <IconCamera size={18} color="#0f0f10" />}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <span className="tr-ellipsis" style={{ fontSize: 13, fontWeight: 500, minWidth: 0 }}>{r.name}</span>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontSize: 11.5, color }}>{label}</span>
            {r.error ? <span style={{ fontSize: 11, color: '#d9342b' }}>Not saved</span> : null}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fafaf9' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 28px 0' }}>
        <Breadcrumb wsName={wsName} locationName={head.group_name ?? head.collection_name ?? 'Collections'} onBack={back} last="Edit details" study={{ name: studyName(head), onOpen: toStudy }} />
      </div>
      <div className="tr-page" style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: '24px 28px 40px' }}>
        <div style={{ width: '100%', maxWidth: 1120, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Notice>Do not enter any PHI, such as patient names, dates of birth, MRNs or accession numbers.</Notice>

          <div style={{ background: '#fff', border: '1px solid #ebebea', borderRadius: 12 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '18px 24px 0' }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Study</h2>
              <span style={{ fontSize: 13, color: '#8a8a8f' }}>shared by every image in it</span>
            </div>
            <div style={{ padding: '14px 24px 22px', display: 'flex', flexDirection: 'column', gap: 16 }} onBlur={() => void editor.getState().flush(STUDY)}>
              <StudyFields values={s.study} onChange={(p) => editor.getState().setStudy(p)} onAgeBlur={(age) => editor.getState().setStudy({ age })} said={saidStudy} />
              {/* The date the study was made. The uploader does not ask (it comes from the file); here it can be corrected. */}
              <div className="tr-stack-sm" style={{ display: 'grid', gridTemplateColumns: 'minmax(180px,1fr) 180px 120px', gap: '14px 16px' }}>
                <Field label="Study date" hint={saidStudy('study_datetime', null)}>
                  <Input type="date" value={s.studyDate} max={new Date().toISOString().slice(0, 10)} onChange={(e) => editor.getState().setStudyDate(e.target.value)} />
                </Field>
              </div>
              {s.studyError && !STUDY_SHOWN.includes(s.studyError.field ?? '') ? <Notice tone="danger">{s.studyError.message}</Notice> : null}
            </div>
          </div>

          <div style={{ background: '#fff', border: '1px solid #ebebea', borderRadius: 12 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '18px 24px 0' }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Images</h2>
              <span style={{ fontSize: 13, color: '#8a8a8f' }}>{s.images ? `${s.images.length} image${s.images.length === 1 ? '' : 's'}` : ''}</span>
            </div>
            {!s.images ? (
              <div style={{ display: 'grid', placeItems: 'center', minHeight: 200, color: s.imagesError ? '#d9342b' : '#8a8a8f', fontSize: 13.5 }}>{s.imagesError ?? 'Loading images…'}</div>
            ) : !s.images.length ? (
              <div style={{ display: 'grid', placeItems: 'center', minHeight: 200, color: '#8a8a8f', fontSize: 13.5 }}>This study has no images yet.</div>
            ) : (
              <div className="tr-stack" style={{ display: 'grid', gridTemplateColumns: '320px minmax(0,1fr)', marginTop: 14, borderTop: '1px solid #f0f0ee' }}>
                <div style={{ background: '#fafaf9', borderRight: '1px solid #f0f0ee', display: 'flex', flexDirection: 'column', borderBottomLeftRadius: 12 }}>{s.images.map(railRow)}</div>
                <div style={{ padding: '16px 24px 22px', display: 'flex', flexDirection: 'column', gap: 14 }} onBlur={() => row && void editor.getState().flush(row.imageId)}>
                  {row ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                          <span className="tr-ellipsis" style={{ fontSize: 14, fontWeight: 600 }}>{row.name}</span>
                          <span style={{ fontSize: 12, color: '#8a8a8f' }}>{row.kind === 'wsi' ? 'Scanned image' : row.kind === 'document' ? 'Document' : 'Flat image'} · details for this image only</span>
                        </div>
                        {isEditable(row) && editableCount > 1 ? (
                          <Button variant="outlined" size={32} icon={<IconApplyAll size={14} />} onClick={() => { const n = editor.getState().applyToAll(); toast(`Details applied to ${n} other ${n === 1 ? 'image' : 'images'}`); }}>
                            Apply to all images
                          </Button>
                        ) : null}
                      </div>
                      {row.stage === 'failed' ? (
                        <Notice tone="danger">
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>Could not be imported</span>
                            <span style={{ fontSize: 12.5 }}>{row.stageError ?? 'The file could not be imported.'}</span>
                          </div>
                        </Notice>
                      ) : !isEditable(row) ? (
                        <Notice>Still uploading or importing. Its details can be edited once the import finishes.</Notice>
                      ) : null}
                      <ImageFields values={row.fields} certain={row.certain} onField={(k, v) => editor.getState().setImageField(row.imageId, k, v)} said={saidImage(row)} disabled={!isEditable(row)} />
                      {row.error && !IMAGE_SHOWN.includes(row.error.field ?? '') ? <Notice tone="danger">{row.error.message}</Notice> : null}
                    </>
                  ) : null}
                </div>
              </div>
            )}
          </div>

          {held && refused ? (
            <Notice tone="danger">
              <span style={{ flex: 1 }}>Some changes could not be saved. Fix the fields marked in red, or leave without them.</span>
              <TextLink onClick={toStudy} color="#8f1d16" fontSize={12.5} underline hover="none">Leave without them</TextLink>
            </Notice>
          ) : null}
          <div className="tr-wrap-sm" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, paddingTop: 4, fontSize: 13 }}>
            {status}
            <Button variant="primary" size={36} disabled={leaving} onClick={() => void done()}>
              Done
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
